// React Hook for Bot Detection
import { logger } from '@/utils/logger';
import { useState, useEffect, useCallback } from 'react';
import { 
  isAutomationBot, 
  getBotDetectionError, 
  getBotDetectionInfo, 
  validateHumanSubmission,
  initializeBotDetection,
  type BotDetectionResult 
} from '../utils/botDetection';

export interface UseBotDetectionReturn {
  isBot: boolean;
  error: string;
  detectionInfo: BotDetectionResult | null;
  checkForBots: () => boolean;
  validateSubmission: () => { isValid: boolean; error: string };
}

export function useBotDetection(): UseBotDetectionReturn {
  const [isBot, setIsBot] = useState(false);
  const [error, setError] = useState('');
  const [detectionInfo, setDetectionInfo] = useState<BotDetectionResult | null>(null);

  // Initialize bot detection on mount
  useEffect(() => {
    initializeBotDetection();
    
    // Initial check
    checkForBots();
  }, []);

  // Function to manually check for bots
  const checkForBots = useCallback((): boolean => {
    try {
      const botDetected = isAutomationBot();
      const errorMessage = botDetected ? getBotDetectionError() : '';
      const info = getBotDetectionInfo();
      
      setIsBot(botDetected);
      setError(errorMessage);
      setDetectionInfo(info);
      
      return botDetected;
    } catch (err) {
      logger.error('Error in bot detection:', err);
      // On error, assume no bot to avoid blocking legitimate users
      setIsBot(false);
      setError('');
      setDetectionInfo(null);
      return false;
    }
  }, []);

  // Function to validate form submission
  const validateSubmission = useCallback(() => {
    try {
      const validation = validateHumanSubmission();
      
      setIsBot(!validation.isValid);
      setError(validation.error);
      setDetectionInfo(validation.details);
      
      return {
        isValid: validation.isValid,
        error: validation.error
      };
    } catch (err) {
      logger.error('Error in submission validation:', err);
      // On error, allow submission to avoid blocking legitimate users
      return {
        isValid: true,
        error: ''
      };
    }
  }, []);

  return {
    isBot,
    error,
    detectionInfo,
    checkForBots,
    validateSubmission
  };
}

// Simplified hook for just checking if bot is detected
export function useSimpleBotDetection(): boolean {
  const [isBot, setIsBot] = useState(false);

  useEffect(() => {
    try {
      const botDetected = isAutomationBot();
      setIsBot(botDetected);
    } catch (err) {
      logger.error('Bot detection error:', err);
      setIsBot(false); // Default to allowing access on error
    }
  }, []);

  return isBot;
}

// Hook for continuous bot monitoring (useful for long forms)
export function useContinuousBotDetection(intervalMs: number = 10000): boolean {
  const [isBot, setIsBot] = useState(false);

  useEffect(() => {
    const checkBot = () => {
      try {
        const botDetected = isAutomationBot();
        setIsBot(botDetected);
      } catch (err) {
        logger.error('Continuous bot detection error:', err);
        setIsBot(false);
      }
    };

    // Initial check
    checkBot();

    // Set up interval
    const intervalId = setInterval(checkBot, intervalMs);

    // Cleanup
    return () => {
      clearInterval(intervalId);
    };
  }, [intervalMs]);

  return isBot;
}